import React, { useState, useEffect } from 'react';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import {
  SeekersPipelineBoard,
  SeekersPipelineList,
  Seeker
} from '../components/SeekersPipeline';
import PackageList, { Package as GuidePackage } from '../components/packages/PackageList';
import PackageForm, { PackageFormData } from '../components/packages/PackageForm';
import { getSupabaseClient } from '../lib/supabase';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatUtcInTimezone } from '../utils/timezoneUtils';

type SeekersPageProps = {
  onViewDetails?: (seeker: Seeker | null) => void;
};

type PipelineRow = {
  match_id: string;
  guide_id: string;
  seeker_id: string;
  match_score: number;
  score_breakdown: Record<string, number> | null;
  reasoning: string[] | null;
  match_created_at: string;
  seeker_name: string | null;
  seeker_email: string | null;
  experience_types: string[] | null;
  support_types: string[] | null;
  latest_session_id: string | null;
  session_type: string | null;
  session_status: string | null;
  session_date: string | null;
  session_time: string | null;
  session_created_at: string | null;
  start_time: string | null;
  pipeline_stage: Seeker['stage'] | null;
};

const formatLocalDateTime = (
  dateValue?: string | null,
  timeValue?: string | null,
  startTime?: string | null,
  timezone?: string,
) => {
  const fmtOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'shortGeneric',
  };

  // Prefer start_time (proper UTC timestamp) when available
  if (startTime && timezone) {
    try {
      return formatUtcInTimezone(startTime, timezone, fmtOpts);
    } catch { /* fall through to legacy */ }
  }

  if (!dateValue) return null;

  if (timeValue) {
    try {
      // session_time is stored as UTC — append Z so Date parses it correctly
      const utcIso = `${dateValue}T${timeValue}Z`;
      if (timezone) {
        return formatUtcInTimezone(utcIso, timezone, fmtOpts);
      }
      return new Date(utcIso).toLocaleString();
    } catch (error) {
      return new Date(dateValue).toLocaleDateString();
    }
  }

  return new Date(dateValue).toLocaleDateString();
};

const formatMatchActivity = (row: PipelineRow, timezone?: string): string => {
  if (row.session_status) {
    const formatted = formatLocalDateTime(row.session_date, row.session_time, row.start_time, timezone);
    const label = row.session_status === 'completed'
      ? 'Last session completed'
      : row.session_status === 'scheduled' || row.session_status === 'confirmed'
        ? 'Consultation scheduled'
        : 'Session update';
    return formatted ? `${label}: ${formatted}` : label;
  }

  if (row.match_created_at && timezone) {
    const matchDate = formatUtcInTimezone(row.match_created_at, timezone, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
    return `Matched on ${matchDate}`;
  }
  const matchDate = row.match_created_at ? new Date(row.match_created_at).toLocaleString() : 'Recently matched';
  return `Matched on ${matchDate}`;
};

const toPipelineSeeker = (
  row: PipelineRow,
  profileLookup?: Map<string, { full_name?: string | null }>,
  hasPackageOffer?: Map<string, boolean>,
  hasPaidOrder?: Map<string, boolean>,
  paidOrderRevenue?: Map<string, number>,
  seekerUserMap?: Map<string, string>,
  allSessionsBySeeker?: Map<string, any[]>,
  timezone?: string,
): Seeker => {
  const profileInfo = profileLookup?.get(row.seeker_id);
  const rawName = profileInfo?.full_name || row.seeker_name || 'Seeker';

  const userId = seekerUserMap?.get(row.seeker_id) ?? row.seeker_id;
  const name = typeof rawName === 'string' && rawName.trim().length > 0 ? rawName : 'Seeker';
  const revenueAmount = paidOrderRevenue?.get(userId);

  // Determine stage based on session, package offer and order status
  // Priority order: paid order > package offer > consultation completed > consultation scheduled > matched
  let stage: Seeker['stage'] = 'matched';

  // Paid order is the highest priority stage (overrides everything)
  if (hasPaidOrder?.get(userId)) {
    stage = 'active_clients';
  }
  // Package offer is next priority
  else if (hasPackageOffer?.get(userId)) {
    stage = 'payment_pending';
  }
  // By default use the session info from pipeline row
  let sessionStageSource: 'row' | 'manual' | null = null;
  let latestSession = row.session_type ? {
    session_type: row.session_type,
    session_status: row.session_status,
    session_date: row.session_date,
    session_time: row.session_time,
    start_time: row.start_time,
  } : null;

  // Pull latest session info from full scheduled_sessions (if available)
  const manualSessions = allSessionsBySeeker?.get(row.seeker_id) ?? [];
  if (manualSessions.length > 0) {
    latestSession = manualSessions[0];
    sessionStageSource = 'manual';
  }

  // Only apply session-based stage logic if we haven't already set a higher priority stage
  // (payment_pending and active_clients take priority over session-based stages)
  if (stage !== 'payment_pending' && stage !== 'active_clients') {
    // Consultation completed → awaiting package
    if (latestSession?.session_type === 'consultation' && latestSession?.session_status === 'completed') {
      stage = 'awaiting_package';
    }
    // Consultation scheduled/confirmed → pending consultation
    else if (
      latestSession?.session_type === 'consultation' &&
      (latestSession?.session_status === 'scheduled' || latestSession?.session_status === 'confirmed')
    ) {
      stage = 'pending_consultation';
    }
    // Otherwise stay as matched
  }

  const title = 'Matched seeker';

  const reasoningSummary = row.reasoning && row.reasoning.length > 0
    ? row.reasoning[0]
    : undefined;

  let notes = reasoningSummary;
  const sessionType = latestSession?.session_type ?? row.session_type;
  const sessionStatus = latestSession?.session_status ?? row.session_status;
  const sessionDate = latestSession?.session_date ?? row.session_date;
  const sessionTime = latestSession?.session_time ?? row.session_time;
  const sessionStartTime = latestSession?.start_time ?? row.start_time;
  const activityRow: PipelineRow = {
    ...row,
    session_type: sessionType,
    session_status: sessionStatus,
    session_date: sessionDate,
    session_time: sessionTime,
    start_time: sessionStartTime,
  };

  if (stage === 'pending_consultation') {
    if (sessionStatus === 'confirmed') {
      notes = 'Consultation confirmed';
    } else if (sessionStatus === 'scheduled') {
      notes = 'Consultation scheduled';
    } else {
      notes = 'Awaiting consultation scheduling';
    }
  } else if (stage === 'awaiting_package') {
    if (sessionStatus === 'completed' && sessionType === 'consultation') {
      notes = 'Consultation completed - awaiting package';
    } else {
      notes = 'Awaiting package';
    }
  } else if (stage === 'payment_pending') {
    notes = 'Package offered - awaiting payment';
  } else if (stage === 'active_clients') {
    notes = 'Active client';
  }

  return {
    id: row.seeker_id,
    userId,
    name,
    title,
    stage,
    lastActivity: formatMatchActivity(activityRow, timezone),
    notes,
    matchScore: row.match_score,
    sessionStatus,
    sessionDate,
    sessionType,
    amount: revenueAmount,
  };
};

const SeekersPage: React.FC<SeekersPageProps> = ({ onViewDetails }) => {
  const [seekers, setSeekers] = useState<Seeker[]>([]);
  const [packages, setPackages] = useState<GuidePackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  const [error, setError] = useState<string | null>(null);
  const [showPackageViewer, setShowPackageViewer] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<GuidePackage | null>(null);
  const [packageFormData, setPackageFormData] = useState<PackageFormData | null>(null);
  const { timezone: guideTimezone } = useGuideTimezone();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const supabase = getSupabaseClient();
        const { data: { user }, error: sessionError } = await supabase.auth.getUser();

        if (sessionError) {
          throw sessionError;
        }

        if (!user) {
          setSeekers([]);
          setPackages([]);
          setLoading(false);
          return;
        }

        const [
          { data: pipelineRows, error: pipelineError },
          { data: packageRows, error: packageError },
          { data: sessionRows, error: sessionsError },
        ] = await Promise.all([
          supabase
            .from('guide_seeker_pipeline')
            .select('*')
            .eq('guide_id', user.id),
          supabase
            .from('guide_packages')
            .select('id, name, description, duration_months, is_active, created_at')
            .eq('guide_id', user.id)
            .order('created_at', { ascending: true }),
          supabase
            .from('scheduled_sessions')
            .select('id, guide_id, seeker_id, session_type, session_status, session_date, session_time, start_time, duration_minutes')
            .eq('guide_id', user.id),
        ]);

        console.log('[SeekersPage] loaded raw pipeline rows:', pipelineRows?.length ?? 0);
        console.log('[SeekersPage] loaded package rows:', packageRows?.length ?? 0);
        console.log('[SeekersPage] loaded sessions rows:', sessionRows?.length ?? 0);

        if (pipelineError) {
          throw pipelineError;
        }

        if (packageError) {
          throw packageError;
        }

        if (sessionsError) {
          console.warn('Unable to load sessions for guide pipeline', sessionsError);
        }

        const pipelineData = (pipelineRows as PipelineRow[] | null) ?? [];
        const sessionsData = Array.isArray(sessionRows) ? sessionRows : [];
        console.log('[SeekersPage] pipelineData count after fallback:', pipelineData.length);
        console.log('[SeekersPage] sessionsData count after fallback:', sessionsData.length);

        const matchIds = pipelineData
          .map(row => row.match_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0);

        let filteredPipelineRows = pipelineData;

        if (matchIds.length > 0) {
          try {
            const { data: matchStatusRows, error: matchStatusError } = await supabase
              .from('matches')
              .select('id, status')
              .in('id', matchIds);

            if (matchStatusError) {
              console.warn('Unable to load match statuses for pipeline view', matchStatusError);
            } else if (matchStatusRows) {
              const statusMap = new Map<string, string | null | undefined>();
              matchStatusRows.forEach(row => {
                if (row?.id) {
                  statusMap.set(row.id, row.status as string | null | undefined);
                }
              });
              filteredPipelineRows = pipelineData.filter(row => {
                const status = statusMap.get(row.match_id);
                return !status || status === 'active';
              });
            }
          } catch (statusErr) {
            console.warn('Unexpected error loading match statuses for pipeline view', statusErr);
          }
        }

        const seekerIds = filteredPipelineRows
          .map(row => row.seeker_id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0);
        const profileLookup = new Map<string, { full_name?: string | null }>();
        const hasPackageOffer = new Map<string, boolean>();
        const hasPaidOrder = new Map<string, boolean>();
        const paidOrderRevenue = new Map<string, number>();
        const seekerUserMap = new Map<string, string>();
        const seekerUserIdSet = new Set<string>();
        let mappingRows: Array<{ seeker_id?: string | null; user_id?: string | null }> = [];

        const userToSeekerMap = new Map<string, string>();

        if (seekerIds.length > 0) {
          try {
            const { data: mappingData, error: mappingError } = await supabase
              .from('seekers')
              .select('seeker_id, user_id')
              .in('seeker_id', seekerIds);

            if (mappingError) {
              console.warn('Unable to load seeker mappings for guide view', mappingError);
            } else if (mappingData) {
              mappingRows = mappingData as typeof mappingRows;
              mappingRows.forEach((row: any) => {
                if (row.seeker_id && row.user_id) {
                  seekerUserMap.set(row.seeker_id, row.user_id);
                  seekerUserIdSet.add(row.user_id);
                  userToSeekerMap.set(row.user_id, row.seeker_id);
                }
              });
            }
          } catch (mappingErr) {
            console.warn('Unexpected error loading seeker mappings for guide view', mappingErr);
          }
        }

        const seekerUserIds = Array.from(seekerUserIdSet);

        if (seekerUserIds.length > 0) {
          try {
            const { data: offerRows, error: offerError } = await supabase
              .from('package_offers')
              .select('seeker_id, status')
              .in('seeker_id', seekerUserIds)
              .eq('guide_id', user.id)
              .eq('status', 'sent');

            if (offerError) {
              console.warn('Unable to load package offers', offerError);
            } else if (offerRows) {
              offerRows.forEach((row: any) => {
                const offerUserId = typeof row.seeker_id === 'string' ? row.seeker_id : row.seeker_id ? String(row.seeker_id) : null;
                if (offerUserId) {
                  hasPackageOffer.set(offerUserId, true);
                }
              });
            }

            const { data: orderRows, error: orderError } = await supabase
              .from('orders')
              .select('seeker_id, status, total_cents')
              .in('seeker_id', seekerUserIds)
              .in('status', ['paid_pending_delivery', 'delivered']);

            if (orderError) {
              console.warn('Unable to load orders', orderError);
            } else if (orderRows) {
              orderRows.forEach((row: any) => {
                const orderUserId = typeof row.seeker_id === 'string' ? row.seeker_id : row.seeker_id ? String(row.seeker_id) : null;
                if (orderUserId) {
                  hasPaidOrder.set(orderUserId, true);
                  const current = paidOrderRevenue.get(orderUserId) ?? 0;
                  const cents = typeof row.total_cents === 'number' ? row.total_cents : Number(row.total_cents ?? 0);
                  paidOrderRevenue.set(orderUserId, current + cents / 100);
                }
              });
            }
          } catch (err) {
            console.warn('Unexpected error loading package offers and orders', err);
          }
        }

        const sessionsBySeeker = new Map<string, any[]>();
        if (sessionsData.length > 0) {
          sessionsData.forEach(session => {
            const userId = typeof session?.seeker_id === 'string'
              ? session.seeker_id
              : session?.seeker_id
              ? String(session.seeker_id)
              : null;
            if (!userId) return;
            const seekerId = userToSeekerMap.get(userId);
            if (!seekerId) return;
            const list = sessionsBySeeker.get(seekerId) ?? [];
            list.push(session);
            sessionsBySeeker.set(seekerId, list);
          });

          sessionsBySeeker.forEach((list, seekerId) => {
            list.sort((a, b) => {
              const aDate = a.start_time
                ? new Date(a.start_time).getTime()
                : a.session_date ? new Date(`${a.session_date}T${a.session_time ?? '00:00:00'}Z`).getTime() : 0;
              const bDate = b.start_time
                ? new Date(b.start_time).getTime()
                : b.session_date ? new Date(`${b.session_date}T${b.session_time ?? '00:00:00'}Z`).getTime() : 0;
              return bDate - aDate;
            });
            const latest = list[0];
            console.log('[SeekersPage] latest session mapped for seeker', seekerId, latest?.session_status, latest?.session_date, latest?.session_time);
          });
        }

        if (mappingRows.length > 0 && seekerUserIds.length > 0) {
          try {
            // NOTE: email and phone are intentionally NOT fetched to protect seeker privacy
            const { data: profileRows, error: profileError } = await supabase
              .from('profiles')
              .select('id, full_name')
              .in('id', seekerUserIds);

            if (profileError) {
              console.warn('Unable to load seeker profiles for guide view', profileError);
            } else if (profileRows) {
              const profileByUser = new Map<string, { full_name?: string | null }>();
              profileRows.forEach((row: any) => {
                if (row.id) {
                  profileByUser.set(row.id, {
                    full_name: row.full_name ?? null,
                  });
                }
              });

              mappingRows.forEach((row: any) => {
                if (row.seeker_id && row.user_id) {
                  const info = profileByUser.get(row.user_id);
                  if (info) {
                    profileLookup.set(row.seeker_id, info);
                    seekerUserMap.set(row.seeker_id, row.user_id);
                  }
                }
              });
            }
          } catch (profileErr) {
            console.warn('Unexpected error loading seeker profiles for guide view', profileErr);
          }
        }
        const seekersData = filteredPipelineRows.map(row =>
          toPipelineSeeker(
            row,
            profileLookup,
            hasPackageOffer,
            hasPaidOrder,
            paidOrderRevenue,
            seekerUserMap,
            sessionsBySeeker,
            guideTimezone,
          )
        );
        seekersData.forEach(seeker => {
          console.log('[SeekersPage] seeker stage derived', seeker.id, seeker.stage, seeker.sessionStatus, seeker.sessionDate, seeker.sessionType);
        });
        setSeekers(seekersData);

        if (packageRows && packageRows.length > 0) {
          const packageIds = packageRows.map((pkg) => pkg.id);
          const packageMap = new Map<string, GuidePackage>();

          packageRows.forEach((pkg: any) => {
            packageMap.set(pkg.id, {
              id: pkg.id,
              name: pkg.name ?? 'Package',
              description: pkg.description ?? '',
              duration_months: pkg.duration_months ?? 3,
              components: [],
              totalCost: 0,
              is_active: pkg.is_active ?? true,
              created_at: pkg.created_at ?? new Date().toISOString()
            });
          });

          if (packageIds.length > 0) {
            const { data: components, error: componentsError } = await supabase
              .from('guide_package_components')
              .select('id, package_id, component_type, name, quantity, unit_price_dollars, sort_order')
              .in('package_id', packageIds);

            if (componentsError) {
              throw componentsError;
            }

            (components ?? []).forEach((component: any) => {
              const pkg = packageMap.get(component.package_id);
              if (!pkg) return;

              const quantity = component.quantity ?? 0;
              const price = component.unit_price_dollars ?? 0;

              pkg.components.push({
                id: component.id ?? '',
                component_type: component.component_type ?? 'additional',
                name: component.name ?? '',
                quantity,
                unit_price_dollars: price,
                sort_order: component.sort_order ?? 0
              });

              pkg.totalCost += quantity * price;
            });

            const { data: totals, error: totalsError } = await supabase
              .from('v_guide_package_totals')
              .select('package_id, total_dollars')
              .in('package_id', packageIds);

            if (totalsError) {
              throw totalsError;
            }

            (totals ?? []).forEach((total: any) => {
              const pkg = packageMap.get(total.package_id);
              if (!pkg) return;
              if (typeof total.total_dollars === 'number') {
                pkg.totalCost = total.total_dollars;
              }
            });

            packageMap.forEach(pkg => {
              pkg.components.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            });
          }

          const sortedPackages = Array.from(packageMap.values()).sort((a, b) => {
            const aDate = new Date(a.created_at).getTime();
            const bDate = new Date(b.created_at).getTime();
            return bDate - aDate;
          });

          setPackages(sortedPackages);
        } else {
          setPackages([]);
        }
      } catch (err) {
        console.error('Failed to load seekers pipeline', err);
        setError(err instanceof Error ? err.message : 'Failed to load seekers');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [guideTimezone]);

  const handleSeekerClick = (seeker: Seeker) => {
    onViewDetails?.(seeker);
  };

  const handleViewPackage = (pkg: GuidePackage) => {
    setSelectedPackage(pkg);
    setPackageFormData({
      name: pkg.name,
      description: pkg.description,
      duration_months: pkg.duration_months,
      components: pkg.components.map(component => ({
        id: component.id,
        component_type: component.component_type,
        name: component.name,
        quantity: component.quantity,
        unit_price_dollars: component.unit_price_dollars,
        sort_order: component.sort_order
      }))
    });
    setShowPackageViewer(true);
  };

  const handleClosePackageViewer = () => {
    setShowPackageViewer(false);
    setSelectedPackage(null);
    setPackageFormData(null);
  };

  const handleReadOnlySetFormData = (data: PackageFormData) => {
    setPackageFormData(data);
  };

  if (loading) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-[#2C2731]/70">Loading seekers...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#2C2731] border border-[#FFFCEF] text-[#FFFCEF] px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-medium font-recoleta text-[#2C2731] mb-1">Seekers Pipeline</h1>
            <p className="text-sm text-[#2C2731]/80">Manage your seekers through their journey stages</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-2 rounded-md text-sm font-medium border border-[#2C2731] transition-colors ${viewMode === 'board' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]'}`}
            >
              Board View
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md border border-[#2C2731] transition-colors ${viewMode === 'list' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]'}`}
            >
              <ListBulletIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Pipeline Views */}
        {viewMode === 'board' ? (
          <SeekersPipelineBoard seekers={seekers} onSeekerClick={handleSeekerClick} />
        ) : (
          <SeekersPipelineList seekers={seekers.filter(s => s.stage !== 'matched')} onSeekerClick={handleSeekerClick} />
        )}

        {/* Saved Packages Section */}
        <div className="mt-8">
          <PackageList
            packages={packages}
            readOnly
            onSelect={handleViewPackage}
          />
        </div>

        {showPackageViewer && selectedPackage && packageFormData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <PackageForm
                formData={packageFormData}
                setFormData={handleReadOnlySetFormData}
                isEditing={false}
                onSave={() => {}}
                onCancel={handleClosePackageViewer}
                readOnly
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeekersPage;
